import {DossierSettings, SectionName} from '@/entities/dossier-settings';
import {Button, Dot, Icon, Tooltip, TooltipContent, TooltipTrigger} from '@/shared/components';

type TooltipSection = {
    sectionName: SectionName;
    label: string;
};

const tooltipSections: TooltipSection[] = [
    { sectionName: "DossierDetails", label: "Details" },
    { sectionName: "VsmOfOpstartplan", label: "VSM en Opstartplan" },
    { sectionName: "Faciliteiten", label: "Faciliteiten" },
    { sectionName: "FaciliteitenLandelijk", label: "Landelijke faciliteiten" },
    { sectionName: "Tvv", label: "Treinvervangend vervoer" },
    { sectionName: "BetrokkenTreinen", label: "Betrokken treinen" },
    { sectionName: "MedewerkersOpStation", label: "Medewerkers op station" },
    { sectionName: "Evaluatie", label: "Evaluatie" },
];

type SectionsTooltipProps = {
    settings: DossierSettings;
};

export function SectionsTooltip({ settings }: SectionsTooltipProps) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    type="button"
                    variant="tertiary"
                    iconOnly
                    icon="info"
                    className="text-content-emphasis"
                    data-testid="secties-tooltip-trigger"
                />
            </TooltipTrigger>
            <TooltipContent
                className="shadow-elevated-3 bg-default max-w-fit overflow-hidden rounded-md p-0"
                arrowClassName="bg-elevated-sunken fill-elevated-sunken"
            >
                <div className="px-3 py-2">
                    <p className="text-content-default text-s mb-3 font-bold">Secties op basis van omschrijving en reikwijdte</p>
                    <ul className="space-y-1">
                        {tooltipSections.map(({ sectionName, label }) => (
                            <SectionsTooltipItem
                                key={sectionName}
                                label={label}
                                isEnabled={DossierSettings.isSectionEnabled(settings, sectionName)}
                                isInUse={DossierSettings.isSectionInUse(settings, sectionName)}
                            />
                        ))}
                    </ul>
                </div>
                <TooltipLegend />
            </TooltipContent>
        </Tooltip>
    );
}

type SectionsTooltipItemProps = {
    label: string;
    isEnabled: boolean;
    isInUse: boolean;
};

function SectionsTooltipItem({ label, isEnabled, isInUse }: SectionsTooltipItemProps) {
    return (
        <li
            className={`text-s flex items-center gap-2 ${isEnabled ? "text-content-default font-medium" : "text-content-subtle font-normal"}`}
            data-enabled={isEnabled}
        >
            <Dot className="border-2" variant={isEnabled ? "emphasis" : "infoOpenSubtle"} size="m" />
            {label}
            {isInUse && (
                <>
                    <Icon name="clipboard" size="xs" className="text-content-subtle" />
                    <span className="text-content-subtle text-xs font-normal italic">Bevat data of berichten</span>
                </>
            )}
        </li>
    );
}

function TooltipLegend() {
    return (
        <div className="bg-elevated-sunken text-content-subtle grid grid-cols-[auto_1fr] gap-x-3 p-3">
            <Dot variant="emphasis" size="m" className="self-center justify-self-center" />
            <p>Wordt getoond</p>
            <Dot variant="infoOpenSubtle" size="m" className="self-center justify-self-center border-2" />
            <p>Wordt niet getoond</p>
            <Icon name="clipboard" size="xs" className="text-content-subtle self-center justify-self-center" />
            <p>Bevat data of berichten</p>
        </div>
    );
}
